import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAnalytics } from '../../api/analytics';
import {
  PanelHeader,
  StatusBanner,
  panelClassName,
} from '../../components/ui';
import {
  IconCalculator,
  IconChart,
  IconClock,
  IconInventory,
  IconOrders,
  IconPriceTag,
  IconProcurement,
} from '../../components/icons';

const RANGE_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 'all', label: 'All time' },
];

const STATUS_ORDER = ['pending', 'unshipped', 'procurement', 'shipped', 'delivered', 'cancelled'];

function formatMoney(value, currency = 'INR', maximumFractionDigits = 0) {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits,
      minimumFractionDigits: 0,
    }).format(value || 0);
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString()}`;
  }
}

function formatDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

function LoadingBar({ className = '' }) {
  return <span className={`block animate-pulse rounded-lg bg-slate-200/70 ${className}`} aria-hidden="true" />;
}

const TONE_STYLES = {
  default: {
    card: 'border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))]',
    icon: 'bg-[rgb(var(--crm-page-bg))] text-[rgb(var(--crm-accent))]',
    glow: 'from-[rgb(var(--crm-accent)/0.12)]',
    bar: 'bg-slate-400',
  },
  blue: {
    card: 'border-sky-500/30 bg-[linear-gradient(145deg,rgb(14_165_233/0.12),rgb(var(--crm-surface))_48%)]',
    icon: 'bg-sky-500/15 text-sky-600',
    glow: 'from-sky-400/30',
    bar: 'bg-sky-500',
  },
  emerald: {
    card: 'border-emerald-500/30 bg-[linear-gradient(145deg,rgb(16_185_129/0.12),rgb(var(--crm-surface))_48%)]',
    icon: 'bg-emerald-500/15 text-emerald-600',
    glow: 'from-emerald-400/30',
    bar: 'bg-emerald-500',
  },
  amber: {
    card: 'border-amber-500/30 bg-[linear-gradient(145deg,rgb(245_158_11/0.14),rgb(var(--crm-surface))_48%)]',
    icon: 'bg-amber-500/15 text-amber-600',
    glow: 'from-amber-400/30',
    bar: 'bg-amber-500',
  },
  violet: {
    card: 'border-violet-500/30 bg-[linear-gradient(145deg,rgb(139_92_246/0.12),rgb(var(--crm-surface))_48%)]',
    icon: 'bg-violet-500/15 text-violet-600',
    glow: 'from-violet-400/30',
    bar: 'bg-violet-500',
  },
  rose: {
    card: 'border-rose-500/30 bg-[linear-gradient(145deg,rgb(244_63_94/0.12),rgb(var(--crm-surface))_48%)]',
    icon: 'bg-rose-500/15 text-rose-600',
    glow: 'from-rose-400/25',
    bar: 'bg-rose-500',
  },
  accent: {
    card: 'border-[rgb(var(--crm-accent)/0.28)] bg-[linear-gradient(145deg,rgb(var(--crm-accent-soft)),rgb(var(--crm-surface))_55%)]',
    icon: 'bg-[rgb(var(--crm-accent-soft))] text-[rgb(var(--crm-accent))]',
    glow: 'from-[rgb(var(--crm-accent)/0.28)]',
    bar: 'bg-[rgb(var(--crm-accent))]',
  },
  success: {
    card: 'border-[rgb(var(--crm-success)/0.28)] bg-[linear-gradient(145deg,rgb(var(--crm-success)/0.12),rgb(var(--crm-surface))_48%)]',
    icon: 'bg-[rgb(var(--crm-success)/0.14)] text-[rgb(var(--crm-success))]',
    glow: 'from-[rgb(var(--crm-success)/0.25)]',
    bar: 'bg-[rgb(var(--crm-success))]',
  },
};

function HighlightCard({ label, value, detail, icon: Icon, tone = 'blue' }) {
  const styles = TONE_STYLES[tone] || TONE_STYLES.blue;
  return (
    <article className={`relative overflow-hidden rounded-xl border p-5 shadow-sm ${styles.card}`}>
      <div className={`pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${styles.glow} to-transparent blur-2xl`} aria-hidden="true" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="crm-text-muted block text-[11px] font-semibold uppercase tracking-wider">{label}</span>
          <strong className="mt-2.5 block truncate text-2xl font-semibold tracking-tight text-[rgb(var(--crm-text))]">{value}</strong>
          {detail ? <p className="crm-text-muted mt-2 text-[13px] leading-relaxed">{detail}</p> : null}
        </div>
        {Icon ? (
          <span className={`inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl ${styles.icon}`} aria-hidden="true">
            <Icon className="h-5 w-5" />
          </span>
        ) : null}
      </div>
      <div className={`mt-4 h-1 w-12 rounded-full ${styles.bar}`} aria-hidden="true" />
    </article>
  );
}

function AccentPanel({ children, className = '', tone = 'accent' }) {
  const bar = (TONE_STYLES[tone] || TONE_STYLES.accent).bar;
  return (
    <div className={`${panelClassName} relative overflow-hidden ${className}`}>
      <div className={`absolute inset-x-0 top-0 h-1 ${bar}`} aria-hidden="true" />
      {children}
    </div>
  );
}

function MetricTile({ label, value, tone = 'default' }) {
  const styles = TONE_STYLES[tone] || TONE_STYLES.default;
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${styles.card}`}>
      <span className="crm-text-muted text-[11px] font-semibold uppercase tracking-wider">{label}</span>
      <strong className="mt-2 block text-xl font-semibold tracking-tight tabular-nums text-[rgb(var(--crm-text))]">{value}</strong>
      <div className={`mt-3 h-1 w-8 rounded-full ${styles.bar}`} aria-hidden="true" />
    </div>
  );
}

function TrendChart({ data, loading, revenueKey, revenueLabel, currency }) {
  const gradientId = useId().replace(/:/g, '');
  const svgRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const width = 800;
  const height = 260;
  const left = 36;
  const right = 16;
  const top = 18;
  const bottom = 34;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const maxOrders = Math.max(...data.map((point) => point.count), 1);
  const maxRevenue = Math.max(...data.map((point) => point[revenueKey] || 0), 1);
  const step = data.length ? plotWidth / data.length : plotWidth;
  const lineCoords = data.map((point, index) => {
    const x = left + step * index + step / 2;
    const y = top + plotHeight - ((point[revenueKey] || 0) / maxRevenue) * plotHeight;
    return { x, y, point };
  });
  const linePoints = lineCoords.map(({ x, y }) => `${x},${y}`).join(' ');
  const areaPoints = lineCoords.length
    ? [
        `${lineCoords[0].x},${top + plotHeight}`,
        ...lineCoords.map(({ x, y }) => `${x},${y}`),
        `${lineCoords[lineCoords.length - 1].x},${top + plotHeight}`,
      ].join(' ')
    : '';
  const totalOrders = data.reduce((sum, point) => sum + point.count, 0);
  const totalRevenue = data.reduce((sum, point) => sum + (point[revenueKey] || 0), 0);
  const hovered = hoverIndex != null ? lineCoords[hoverIndex] : null;
  const tooltipOnLeft = hovered ? hovered.x > width * 0.62 : false;

  const handlePointerMove = (event) => {
    const svg = svgRef.current;
    if (!svg || !lineCoords.length) return;
    const rect = svg.getBoundingClientRect();
    if (!rect.width) return;
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const x = ((clientX - rect.left) / rect.width) * width;
    if (x < left || x > width - right) {
      setHoverIndex(null);
      return;
    }
    let bestIndex = 0;
    let bestDistance = Infinity;
    lineCoords.forEach((coord, index) => {
      const distance = Math.abs(coord.x - x);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    setHoverIndex(bestIndex);
  };

  return (
    <AccentPanel tone="emerald" className="overflow-hidden">
      <PanelHeader
        title="Performance trend"
        subtitle={loading ? 'Loading performance data…' : `${totalOrders.toLocaleString('en-IN')} orders in the selected period.`}
        action={(
          <div className="hidden items-center gap-4 text-xs sm:flex">
            <span className="flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-sky-700">
              <span className="h-2.5 w-2.5 rounded-sm bg-sky-500/60" />
              Orders
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
              <span className="h-0.5 w-4 rounded bg-emerald-500" />
              {revenueLabel}
            </span>
          </div>
        )}
      />
      <div className="px-4 pb-5 pt-3 sm:px-5">
        {loading ? (
          <div className="flex h-[250px] items-end gap-2 rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg)/0.35)] px-3 pb-2">
            {Array.from({ length: 18 }).map((_, index) => (
              <span key={index} className="flex-1 animate-pulse rounded-t bg-slate-200/70" style={{ height: `${15 + ((index * 19) % 70)}%` }} />
            ))}
          </div>
        ) : data.length ? (
          <>
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--crm-border))] bg-[linear-gradient(180deg,rgb(var(--crm-surface)),rgb(var(--crm-accent-soft)/0.22))]">
              <div className="relative min-w-[34rem] px-2 pt-2">
                <svg
                  ref={svgRef}
                  className="h-auto w-full cursor-crosshair"
                  viewBox={`0 0 ${width} ${height}`}
                  role="img"
                  aria-label={`Orders and ${revenueLabel} trend`}
                  onMouseMove={handlePointerMove}
                  onMouseLeave={() => setHoverIndex(null)}
                  onTouchStart={handlePointerMove}
                  onTouchMove={handlePointerMove}
                  onTouchEnd={() => setHoverIndex(null)}
                >
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(var(--crm-success) / 0.2)" />
                      <stop offset="100%" stopColor="rgb(var(--crm-surface) / 0)" />
                    </linearGradient>
                  </defs>
                  {[0, 1, 2, 3, 4].map((line) => {
                    const y = top + (plotHeight / 4) * line;
                    return (
                      <line
                        key={line}
                        x1={left}
                        x2={width - right}
                        y1={y}
                        y2={y}
                        stroke="rgb(var(--crm-border))"
                        strokeWidth="1"
                        strokeDasharray={line === 4 ? undefined : '3 4'}
                      />
                    );
                  })}
                  {data.map((point, index) => {
                    const barWidth = Math.max(2, Math.min(step * 0.58, 14));
                    const barHeight = (point.count / maxOrders) * plotHeight;
                    const x = left + step * index + (step - barWidth) / 2;
                    const active = hoverIndex === index;
                    const dimmed = hoverIndex != null && !active;
                    return (
                      <rect
                        key={point.date}
                        x={x}
                        y={top + plotHeight - barHeight}
                        width={barWidth}
                        height={Math.max(barHeight, 1)}
                        rx="2"
                        fill="rgb(var(--crm-accent))"
                        opacity={active ? 0.5 : dimmed ? 0.12 : 0.22}
                      />
                    );
                  })}
                  <polygon points={areaPoints} fill={`url(#${gradientId})`} opacity={hoverIndex == null ? 1 : 0.55} />
                  <polyline
                    points={linePoints}
                    fill="none"
                    stroke="rgb(var(--crm-success))"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={hoverIndex == null ? 1 : 0.85}
                  />
                  {lineCoords.map(({ x, y, point }, index) => {
                    const active = hoverIndex === index;
                    if (!active && (hoverIndex != null || data.length > 32)) return null;
                    return (
                      <circle
                        key={point.date}
                        cx={x}
                        cy={y}
                        r={active ? 4.5 : 2.6}
                        fill="rgb(var(--crm-surface))"
                        stroke="rgb(var(--crm-success))"
                        strokeWidth={active ? 2.5 : 1.75}
                      />
                    );
                  })}
                  {hovered ? (
                    <>
                      <line
                        x1={hovered.x}
                        x2={hovered.x}
                        y1={top}
                        y2={top + plotHeight}
                        stroke="rgb(var(--crm-accent))"
                        strokeWidth="1.25"
                        strokeDasharray="3 5"
                        opacity="0.55"
                      />
                      <circle cx={hovered.x} cy={hovered.y} r="8" fill="rgb(var(--crm-success) / 0.12)" />
                      <circle
                        cx={hovered.x}
                        cy={hovered.y}
                        r="4.5"
                        fill="rgb(var(--crm-surface))"
                        stroke="rgb(var(--crm-success))"
                        strokeWidth="2.5"
                      />
                    </>
                  ) : null}
                  <text x={left} y={height - 8} fill="rgb(var(--crm-text-muted))" fontSize="11">{formatDate(data[0].date)}</text>
                  <text x={width - right} y={height - 8} fill="rgb(var(--crm-text-muted))" fontSize="11" textAnchor="end">{formatDate(data[data.length - 1].date)}</text>
                </svg>

                {hovered ? (
                  <div
                    className={`pointer-events-none absolute top-3 z-10 w-[11.75rem] rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface)/0.96)] px-3.5 py-3 shadow-[0_8px_24px_rgb(var(--crm-shadow)/0.12)] backdrop-blur-sm ${
                      tooltipOnLeft ? '-translate-x-full' : ''
                    }`}
                    style={{
                      left: `${(hovered.x / width) * 100}%`,
                      marginLeft: tooltipOnLeft ? '-10px' : '10px',
                    }}
                    role="status"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--crm-text-muted))]">
                      {formatDate(hovered.point.date)}
                    </p>
                    <div className="mt-2.5 grid gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-1.5 text-[rgb(var(--crm-text-muted))]">
                          <span className="h-2 w-2 rounded-sm bg-[rgb(var(--crm-accent))]/55" aria-hidden="true" />
                          Orders
                        </span>
                        <strong className="text-sm tabular-nums text-[rgb(var(--crm-text))]">
                          {hovered.point.count.toLocaleString('en-IN')}
                        </strong>
                      </div>
                      <div className="flex items-center justify-between gap-3 border-t border-[rgb(var(--crm-border))] pt-2">
                        <span className="flex items-center gap-1.5 text-[rgb(var(--crm-text-muted))]">
                          <span className="h-0.5 w-3 rounded bg-[rgb(var(--crm-success))]" aria-hidden="true" />
                          Revenue
                        </span>
                        <strong className="text-sm tabular-nums text-[rgb(var(--crm-text))]">
                          {formatMoney(hovered.point[revenueKey], currency)}
                        </strong>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap justify-between gap-2 text-xs">
              <span className="crm-text-muted">Move across the chart to inspect each period</span>
              <strong className="tabular-nums">{formatMoney(totalRevenue, currency)} total revenue</strong>
            </div>
          </>
        ) : (
          <div className="crm-text-muted grid h-[250px] place-items-center rounded-xl border border-dashed border-[rgb(var(--crm-border))] text-sm">
            No trend data for this period.
          </div>
        )}
      </div>
    </AccentPanel>
  );
}

function StatusPipeline({ distribution, totalOrders }) {
  const lookup = new Map(distribution.map((entry) => [entry.status, entry.count]));
  const known = STATUS_ORDER.map((status) => ({ status, count: lookup.get(status) || 0 }));
  const other = distribution.filter((entry) => !STATUS_ORDER.includes(entry.status));
  const rows = [...known, ...other];
  const maxCount = Math.max(...rows.map((entry) => entry.count), 1);
  const statusStyle = {
    pending: { bar: 'bg-amber-400', chip: 'bg-amber-50 text-amber-800' },
    unshipped: { bar: 'bg-orange-500', chip: 'bg-orange-50 text-orange-800' },
    procurement: { bar: 'bg-violet-500', chip: 'bg-violet-50 text-violet-800' },
    shipped: { bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-800' },
    delivered: { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800' },
    cancelled: { bar: 'bg-rose-400', chip: 'bg-rose-50 text-rose-800' },
  };

  return (
    <AccentPanel tone="violet" className="overflow-hidden">
      <PanelHeader title="Order pipeline" subtitle="Full status distribution for the selected period." />
      <div className="grid gap-4 p-5">
        {rows.map((entry) => {
          const share = totalOrders ? Math.round((entry.count / totalOrders) * 100) : 0;
          const style = statusStyle[entry.status] || { bar: 'bg-[rgb(var(--crm-accent))]', chip: 'bg-[rgb(var(--crm-page-bg))] text-[rgb(var(--crm-text))]' };
          return (
            <div key={entry.status} className="grid grid-cols-[7.5rem_1fr_auto] items-center gap-3">
              <span className={`truncate rounded-md px-2 py-1 text-xs font-semibold capitalize tracking-wide ${style.chip}`}>
                {entry.status}
              </span>
              <div className="h-2.5 overflow-hidden rounded-full bg-[rgb(var(--crm-page-bg))]">
                <div
                  className={`h-full rounded-full transition-all ${style.bar}`}
                  style={{ width: `${entry.count ? Math.max((entry.count / maxCount) * 100, 3) : 0}%` }}
                />
              </div>
              <span className="w-[4.5rem] text-right text-sm font-semibold tabular-nums">
                {entry.count}
                <span className="crm-text-muted ml-1 text-xs font-normal">({share}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </AccentPanel>
  );
}

function AttentionPanel({ attention }) {
  const items = [
    { label: 'Pending procurement', detail: 'Unshipped orders awaiting purchase', value: attention.pendingProcurement, to: '/admin/procurement', icon: IconProcurement, urgent: true, tone: 'amber' },
    { label: 'Low-stock SKUs', detail: 'At or below reorder level', value: attention.lowStockCount, to: '/admin/inventory', icon: IconInventory, urgent: true, tone: 'rose' },
    { label: 'Price changes', detail: 'Listings and tracked ASINs in period', value: attention.priceChanges, to: '/admin/products', icon: IconPriceTag, tone: 'violet' },
  ];

  return (
    <AccentPanel tone="amber" className="overflow-hidden">
      <PanelHeader title="Needs attention" subtitle="Current operational signals with direct actions." />
      <div className="grid gap-2 p-3">
        {items.map((item) => {
          const Icon = item.icon;
          const urgent = item.urgent && item.value > 0;
          const styles = TONE_STYLES[item.tone] || TONE_STYLES.amber;
          return (
            <Link
              key={item.label}
              to={item.to}
              className={`group flex items-center gap-4 rounded-xl border px-3.5 py-3.5 no-underline transition hover:shadow-sm ${styles.card}`}
            >
              <span className={`inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl ${styles.icon}`}><Icon className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1">
                <strong className="block text-sm font-medium text-[rgb(var(--crm-text))]">{item.label}</strong>
                <span className="crm-text-muted mt-0.5 block text-xs">{item.detail}</span>
              </span>
              <span className={`text-xl font-semibold tabular-nums ${urgent ? 'text-amber-700' : 'text-[rgb(var(--crm-text))]'}`}>
                {item.value}
              </span>
              <span className="crm-text-muted transition group-hover:translate-x-0.5 group-hover:text-[rgb(var(--crm-accent))]">→</span>
            </Link>
          );
        })}
      </div>
    </AccentPanel>
  );
}

function FulfillmentSla({ fulfillment, attention, totalOrders, loading }) {
  const onTimeRate = fulfillment.slaEligibleShipments
    ? Math.round((fulfillment.onTimeShipments / fulfillment.slaEligibleShipments) * 100)
    : 0;
  const trackingCoverage = totalOrders
    ? Math.round((fulfillment.measuredShipments / totalOrders) * 100)
    : 0;
  const deliveryCoverage = fulfillment.measuredShipments
    ? Math.round((fulfillment.measuredDeliveries / fulfillment.measuredShipments) * 100)
    : 0;
  const donutStyle = {
    background: fulfillment.slaEligibleShipments
      ? `conic-gradient(rgb(var(--crm-success)) 0 ${onTimeRate}%, rgb(var(--crm-page-bg)) ${onTimeRate}% 100%)`
      : 'rgb(var(--crm-page-bg))',
  };
  const riskRows = [
    { label: 'Overdue', detail: 'Ship-by deadline has passed', value: attention.overdueUnshipped, tone: 'rose' },
    { label: 'Due within 24 hours', detail: 'Requires immediate action', value: attention.dueWithin24Hours, tone: 'amber' },
    { label: 'Due within 48 hours', detail: 'Approaching ship-by deadline', value: attention.dueWithin48Hours, tone: 'blue' },
    { label: 'Missing ship-by date', detail: 'Cannot be evaluated for urgency', value: attention.missingShipByDate, tone: 'violet' },
  ];

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(22rem,0.75fr)]">
      <AccentPanel tone="emerald" className="overflow-hidden">
        <PanelHeader
          title="Fulfilment SLA"
          subtitle="Measured from the first tracking update pushed to Amazon."
          action={<span className="crm-role-badge">{fulfillment.slaEligibleShipments} measurable shipments</span>}
        />
        {loading ? (
          <div className="grid gap-5 p-5 sm:grid-cols-[auto_1fr]">
            <LoadingBar className="h-40 w-40 rounded-full" />
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => <LoadingBar key={index} className="h-20 w-full" />)}
            </div>
          </div>
        ) : (
          <div className="grid gap-6 p-5 sm:grid-cols-[auto_1fr] sm:items-center">
            <div className="relative mx-auto h-40 w-40 rounded-full p-3 shadow-inner" style={donutStyle}>
              <div className="crm-surface grid h-full w-full place-items-center rounded-full border border-[rgb(var(--crm-border))] text-center shadow-sm">
                <div>
                  <strong className="block text-3xl tracking-tight text-emerald-700">{fulfillment.slaEligibleShipments ? `${onTimeRate}%` : '—'}</strong>
                  <span className="crm-text-muted text-xs">shipped on time</span>
                </div>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <MetricTile label="Order → tracking push" value={fulfillment.averageOrderToShipHours ? `${fulfillment.averageOrderToShipHours.toFixed(1)} hrs` : '—'} tone="blue" />
              <MetricTile label="Tracking push → delivery" value={fulfillment.averageShipToDeliveryDays ? `${fulfillment.averageShipToDeliveryDays.toFixed(1)} days` : '—'} tone="violet" />
              <MetricTile label="Order → delivery" value={fulfillment.averageOrderToDeliveryDays ? `${fulfillment.averageOrderToDeliveryDays.toFixed(1)} days` : '—'} tone="amber" />
              <MetricTile label="On-time / late" value={`${fulfillment.onTimeShipments} / ${fulfillment.lateShipments}`} tone="emerald" />
              <div className="sm:col-span-2 rounded-xl border border-emerald-200/70 bg-gradient-to-r from-emerald-50/80 to-sky-50/60 p-4">
                <div className="mb-2 flex justify-between gap-3 text-xs">
                  <span className="crm-text-muted">Local tracking coverage</span>
                  <strong className="tabular-nums">{trackingCoverage}% · Delivery coverage {deliveryCoverage}%</strong>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-white/80">
                  <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-sky-500" style={{ width: `${trackingCoverage}%` }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </AccentPanel>

      <AccentPanel tone="rose" className="overflow-hidden">
        <PanelHeader
          title="Shipping risk"
          subtitle="Current unshipped orders grouped by urgency."
          action={<Link to="/admin/unshipped-orders" className="crm-link text-sm">Open queue →</Link>}
        />
        <div className="grid gap-2 p-3">
          {riskRows.map((row) => {
            const styles = TONE_STYLES[row.tone] || TONE_STYLES.default;
            return (
              <div key={row.label} className={`flex items-center gap-4 rounded-xl border px-3.5 py-3.5 ${styles.card}`}>
                <span className={`inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl ${styles.icon}`}><IconClock className="h-5 w-5" /></span>
                <span className="min-w-0 flex-1">
                  <strong className="block text-sm font-medium">{row.label}</strong>
                  <span className="crm-text-muted mt-0.5 block text-xs">{row.detail}</span>
                </span>
                <strong className="text-xl tabular-nums">{row.value}</strong>
              </div>
            );
          })}
        </div>
      </AccentPanel>
    </div>
  );
}

function ProfitabilityPanel({ profitability, fxRate, onFxRateChange, loading }) {
  const convertedCost = profitability.purchaseCostUsd * fxRate;
  const grossProfit = profitability.salesInr - convertedCost;
  const margin = profitability.salesInr ? (grossProfit / profitability.salesInr) * 100 : 0;
  const coverage = profitability.confirmedLines
    ? Math.round((profitability.pricedLines / profitability.confirmedLines) * 100)
    : 0;
  const hasPricedLines = profitability.pricedLines > 0;

  return (
    <AccentPanel tone="accent" className="overflow-hidden">
      <PanelHeader
        title="Profitability snapshot"
        subtitle="Estimated from confirmed procurement lines with a buying price and a resolvable selling/order price."
        action={(
          <label className="grid gap-1 text-right">
            <span className="crm-text-muted text-[10px] font-semibold uppercase tracking-wide">USD → INR rate</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={fxRate || ''}
              onChange={(event) => onFxRateChange(event.target.value)}
              placeholder="Enter rate"
              className="crm-input h-9 min-h-0 w-28 text-right"
              aria-label="USD to INR exchange rate"
            />
          </label>
        )}
      />
      <div className="grid gap-4 p-5 sm:grid-cols-2">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => <LoadingBar key={index} className="h-24 w-full" />)
        ) : (
          <>
            <MetricTile label="Recorded sales value" value={formatMoney(profitability.salesInr, 'INR')} tone="blue" />
            <MetricTile label="Recorded purchase cost" value={formatMoney(profitability.purchaseCostUsd, 'USD', 2)} tone="amber" />
            <MetricTile
              label="Converted procurement cost"
              value={fxRate > 0 ? formatMoney(convertedCost, 'INR') : 'Set FX rate'}
              tone="violet"
            />
            <MetricTile
              label="Estimated gross margin"
              value={fxRate > 0 ? `${margin.toFixed(1)}%` : 'Set FX rate'}
              tone={fxRate > 0 && grossProfit >= 0 ? 'emerald' : 'rose'}
            />
          </>
        )}
        <div className="sm:col-span-2 rounded-xl border border-[rgb(var(--crm-accent)/0.2)] bg-gradient-to-r from-[rgb(var(--crm-accent-soft)/0.8)] to-emerald-50/50 p-4">
          <div className="mb-2 flex items-center justify-between gap-3 text-xs">
            <span className="crm-text-muted">Cost-data coverage</span>
            <strong className="tabular-nums">{profitability.pricedLines} of {profitability.confirmedLines} confirmed lines · {coverage}%</strong>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-white/70">
            <div className="h-full rounded-full bg-gradient-to-r from-[rgb(var(--crm-accent))] to-emerald-500 transition-all" style={{ width: `${coverage}%` }} />
          </div>
          <p className="crm-text-muted mt-2 text-xs leading-relaxed">
            {hasPricedLines
              ? 'Profit excludes fees, shipping, taxes, and lines without both prices. Selling price falls back to the linked order item when not stored on the procurement line.'
              : 'No priced lines yet. Confirm India procurement lines that have a buying price and a linked order with a selling/unit price.'}
          </p>
        </div>
      </div>
    </AccentPanel>
  );
}

function TopProductsChart({ products, loading }) {
  const [metric, setMetric] = useState('revenue');
  const rankedProducts = [...products]
    .sort((left, right) => metric === 'revenue'
      ? right.revenue - left.revenue
      : right.unitsSold - left.unitsSold)
    .slice(0, 20);
  const maxValue = Math.max(
    ...rankedProducts.map((product) => metric === 'revenue' ? product.revenue : product.unitsSold),
    1
  );

  return (
    <AccentPanel tone="blue" className="overflow-hidden">
      <PanelHeader
        title="Top 20 high-performing products"
        subtitle={`Ranked by ${metric === 'revenue' ? 'sales revenue' : 'units sold'} in the selected period.`}
        action={(
          <div className="inline-flex rounded-lg border border-sky-200 bg-sky-50/70 p-1">
            {[
              { value: 'revenue', label: 'Revenue' },
              { value: 'units', label: 'Units' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setMetric(option.value)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${metric === option.value ? 'bg-white text-sky-800 shadow-sm' : 'text-sky-700/70 hover:text-sky-900'}`}
                aria-pressed={metric === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      />
      <div className="p-5">
        {loading ? (
          <div className="grid gap-x-8 gap-y-5 lg:grid-cols-2">
            {Array.from({ length: 10 }).map((_, index) => <LoadingBar key={index} className="h-14 w-full" />)}
          </div>
        ) : rankedProducts.length ? (
          <div className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
            {rankedProducts.map((product, index) => {
              const value = metric === 'revenue' ? product.revenue : product.unitsSold;
              const width = Math.max((value / maxValue) * 100, 2);
              const rankTone = index < 3 ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800';
              return (
                <div key={product.key} className="min-w-0 rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50/70 via-[rgb(var(--crm-surface))] to-emerald-50/40 p-3.5 shadow-sm">
                  <div className="mb-2.5 flex items-start justify-between gap-4 text-sm">
                    <div className="min-w-0">
                      <strong className="block truncate font-medium" title={product.title}>
                        <span className={`mr-2 inline-flex h-5 min-w-5 items-center justify-center rounded px-1 text-[11px] font-semibold tabular-nums ${rankTone}`}>
                          {index + 1}
                        </span>
                        {product.title}
                      </strong>
                      <span className="crm-text-muted mt-1 block truncate text-xs">
                        {[
                          product.asin ? `ASIN ${product.asin}` : null,
                          product.sku ? `SKU ${product.sku}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || `${product.orderCount} orders`}
                      </span>
                    </div>
                    <strong className="shrink-0 tabular-nums text-sky-900">
                      {metric === 'revenue'
                        ? formatMoney(product.revenue, product.currency)
                        : `${product.unitsSold.toLocaleString('en-IN')} units`}
                    </strong>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/80">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-sky-500 via-[rgb(var(--crm-accent))] to-emerald-500 transition-all duration-300"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="crm-text-muted grid place-items-center rounded-xl border border-dashed border-sky-200 bg-sky-50/40 py-12 text-sm">
            No product sales data for this period.
          </div>
        )}
      </div>
    </AccentPanel>
  );
}

function InventoryHealth({ summary, inventoryValue }) {
  const rows = [
    {
      label: 'Active SKUs',
      detail: 'Products tracked in inventory',
      value: (summary?.inventoryCount ?? 0).toLocaleString('en-IN'),
      icon: IconInventory,
      tone: 'blue',
    },
    {
      label: 'Units on hand',
      detail: 'Available inventory quantity',
      value: (summary?.inventoryUnits ?? 0).toLocaleString('en-IN'),
      icon: IconOrders,
      tone: 'emerald',
    },
    {
      label: 'Low-stock SKUs',
      detail: 'At or below reorder level',
      value: summary?.lowStockCount ?? 0,
      icon: IconClock,
      tone: (summary?.lowStockCount ?? 0) > 0 ? 'amber' : 'violet',
    },
    {
      label: 'Inventory value',
      detail: 'Calculated at unit cost',
      value: formatMoney(inventoryValue, 'USD'),
      icon: IconCalculator,
      tone: 'accent',
    },
  ];

  return (
    <AccentPanel tone="violet" className="overflow-hidden">
      <PanelHeader title="Inventory health" subtitle="Current stock position across active products." />
      <div className="grid gap-3 p-5">
        {rows.map((row) => {
          const Icon = row.icon;
          const styles = TONE_STYLES[row.tone] || TONE_STYLES.blue;
          return (
            <div
              key={row.label}
              className={`flex items-center gap-3 rounded-xl border px-3.5 py-3.5 shadow-sm ${styles.card}`}
            >
              <span className={`inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl ${styles.icon}`}><Icon className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1">
                <strong className="block text-sm font-medium">{row.label}</strong>
                <span className="crm-text-muted mt-0.5 block text-xs">{row.detail}</span>
              </span>
              <strong className="text-lg tabular-nums">{row.value}</strong>
            </div>
          );
        })}
      </div>
    </AccentPanel>
  );
}

function readFxRate() {
  try {
    const stored = Number(window.localStorage.getItem('crm_analytics_usd_inr_rate'));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
}

function AnalyticsPage({ token, loadSummary, scopeLabel }) {
  const [days, setDays] = useState(30);
  const [fxRate, setFxRate] = useState(readFxRate);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState({ type: 'loading', message: '' });
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadAnalytics = useCallback((signal) => {
    setStatus((current) => ({ type: current.type === 'ready' ? 'refreshing' : 'loading', message: '' }));
    const request = loadSummary
      ? loadSummary(days)
      : fetchAnalytics(token, { days });
    return request
      .then((payload) => {
        if (signal?.aborted) return;
        setSummary(payload.summary);
        setStatus({ type: 'ready', message: '' });
        setLastUpdated(new Date());
      })
      .catch((error) => {
        if (signal?.aborted) return;
        setStatus({ type: 'error', message: error.message });
      });
  }, [days, token, loadSummary]);

  useEffect(() => {
    const controller = new AbortController();
    loadAnalytics(controller.signal);
    return () => controller.abort();
  }, [loadAnalytics]);

  useEffect(() => {
    try {
      if (fxRate > 0) {
        window.localStorage.setItem('crm_analytics_usd_inr_rate', String(fxRate));
      } else {
        window.localStorage.removeItem('crm_analytics_usd_inr_rate');
      }
    } catch {
      // Ignore unavailable browser storage.
    }
  }, [fxRate]);

  const metrics = useMemo(() => {
    const marketplaces = summary?.marketplaces ?? [];
    const india = marketplaces.find((entry) => entry.marketplace === 'IN') || { orders: 0, revenue: 0, currency: 'INR' };
    const profitability = summary?.profitability ?? {
      confirmedLines: 0,
      pricedLines: 0,
      pricedUnits: 0,
      salesInr: 0,
      purchaseCostUsd: 0,
    };
    const grossProfit = fxRate > 0
      ? profitability.salesInr - (profitability.purchaseCostUsd * fxRate)
      : null;
    const grossMargin = grossProfit != null && profitability.salesInr
      ? (grossProfit / profitability.salesInr) * 100
      : grossProfit != null
        ? 0
        : null;
    return {
      india,
      totalOrders: india.orders,
      indiaAov: india.orders ? india.revenue / india.orders : 0,
      inventoryValue: summary?.inventoryValue ?? 0,
      profitability,
      grossProfit,
      grossMargin,
    };
  }, [fxRate, summary]);

  const isLoading = status.type === 'loading';
  const isRefreshing = status.type === 'refreshing';
  const rangeLabel = RANGE_OPTIONS.find((option) => option.value === days)?.label || 'Selected period';

  return (
    <section className="grid gap-6">
      <div className="relative overflow-hidden rounded-2xl border border-sky-200/70 bg-[linear-gradient(135deg,rgb(var(--crm-surface))_0%,rgb(var(--crm-accent-soft)/0.75)_45%,rgb(16_185_129/0.12)_100%)] p-5 shadow-sm sm:p-6">
        <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-sky-300/25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-16 left-1/3 h-36 w-36 rounded-full bg-emerald-300/20 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">
              Operations overview
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-[rgb(var(--crm-text))]">
              Analytics
            </h1>
            <p className="crm-text-muted mt-1 max-w-xl text-sm leading-relaxed">
              Revenue, profitability, fulfilment, and inventory signals for {scopeLabel || 'your India marketplace'} — {rangeLabel.toLowerCase()}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex flex-wrap rounded-xl border border-sky-200/80 bg-white/80 p-1 shadow-sm backdrop-blur">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDays(option.value)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${days === option.value ? 'bg-[rgb(var(--crm-accent))] text-white shadow-sm' : 'text-sky-800/70 hover:text-sky-950'}`}
                  aria-pressed={days === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {lastUpdated ? (
                <span className="crm-text-muted hidden rounded-lg border border-emerald-200/80 bg-emerald-50/80 px-3 py-2 text-xs text-emerald-800 sm:inline">
                  Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              ) : null}
              <button type="button" className="crm-btn-secondary gap-2" onClick={() => loadAnalytics()} disabled={isLoading || isRefreshing}>
                <svg className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20 7V3M20 3H16M20 3L16.5 6.5A8 8 0 1 0 19.4 15" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {isRefreshing ? 'Refreshing' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <StatusBanner
        status={status.type === 'error' ? status : { type: 'idle', message: '' }}
        setStatus={setStatus}
        action={
          status.type === 'error' ? (
            <button type="button" className="font-semibold underline underline-offset-2" onClick={() => loadAnalytics()}>
              Try again
            </button>
          ) : null
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <HighlightCard
          label="India revenue"
          value={isLoading ? <LoadingBar className="h-8 w-36" /> : formatMoney(metrics.india.revenue, metrics.india.currency)}
          detail={`${metrics.india.orders.toLocaleString('en-IN')} orders · ${formatMoney(metrics.indiaAov, 'INR')} average`}
          icon={IconChart}
          tone="blue"
        />
        <HighlightCard
          label="Estimated gross profit"
          value={
            isLoading
              ? <LoadingBar className="h-8 w-32" />
              : metrics.grossProfit == null
                ? 'Set FX rate'
                : formatMoney(metrics.grossProfit, 'INR')
          }
          detail={
            metrics.grossProfit == null
              ? 'Enter a USD → INR rate in Profitability snapshot'
              : metrics.profitability.pricedLines === 0
                ? 'No confirmed lines with both buying and selling/order prices'
                : 'Recorded sales less converted purchase cost'
          }
          icon={IconCalculator}
          tone={metrics.grossProfit != null && metrics.grossProfit < 0 ? 'rose' : 'emerald'}
        />
        <HighlightCard
          label="Estimated gross margin"
          value={
            isLoading
              ? <LoadingBar className="h-8 w-24" />
              : metrics.grossMargin == null
                ? 'Set FX rate'
                : `${metrics.grossMargin.toFixed(1)}%`
          }
          detail={
            metrics.grossMargin == null
              ? 'Enter a USD → INR rate in Profitability snapshot'
              : `${metrics.profitability.pricedLines} of ${metrics.profitability.confirmedLines} confirmed lines priced`
          }
          icon={IconChart}
          tone={metrics.grossMargin != null && metrics.grossMargin < 0 ? 'amber' : 'violet'}
        />
        <HighlightCard
          label="Orders in period"
          value={isLoading ? <LoadingBar className="h-8 w-20" /> : metrics.totalOrders.toLocaleString('en-IN')}
          detail="Synced Amazon India orders"
          icon={IconOrders}
          tone="accent"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.65fr)_minmax(20rem,0.75fr)]">
        <TrendChart
          data={summary?.orderTrend ?? []}
          loading={isLoading}
          revenueKey="inRevenue"
          revenueLabel="India revenue"
          currency="INR"
        />
        <InventoryHealth summary={summary} inventoryValue={metrics.inventoryValue} />
      </div>

      <ProfitabilityPanel
        profitability={metrics.profitability}
        fxRate={fxRate}
        onFxRateChange={(value) => setFxRate(Math.max(0, Number(value) || 0))}
        loading={isLoading}
      />

      <FulfillmentSla
        fulfillment={summary?.fulfillment ?? {
          measuredShipments: 0,
          slaEligibleShipments: 0,
          onTimeShipments: 0,
          lateShipments: 0,
          measuredDeliveries: 0,
          averageOrderToShipHours: 0,
          averageShipToDeliveryDays: 0,
          averageOrderToDeliveryDays: 0,
        }}
        attention={summary?.attention ?? {
          overdueUnshipped: 0,
          dueWithin24Hours: 0,
          dueWithin48Hours: 0,
          missingShipByDate: 0,
        }}
        totalOrders={metrics.totalOrders}
        loading={isLoading}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.85fr)]">
        <StatusPipeline distribution={summary?.statusDistribution ?? []} totalOrders={metrics.totalOrders} />
        <AttentionPanel attention={summary?.attention ?? { atRiskUnshipped: 0, pendingProcurement: 0, lowStockCount: 0, priceChanges: 0 }} />
      </div>

      <TopProductsChart products={summary?.topProducts ?? []} loading={isLoading} />
    </section>
  );
}

export default AnalyticsPage;
